import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { HiOutlineClock, HiOutlineSearch } from 'react-icons/hi';
import { useSearch } from '@/hooks/api/useSearchQueries';
import { SearchResultIcon } from '@/components/common/SearchResultIcon';

/**
 * Global search typeahead — spec 7.3, ported from the v18 block (:6225-6437).
 *
 * Live on every keystroke, NOT Enter-only. Searches courses, categories,
 * instructors, articles, resources, bundles and books; grouped by type;
 * matched text highlighted; ranking prefix > word-start > substring (done
 * server-side, see services/search.ts); ↑/↓/Enter/Escape; recent + trending
 * when empty; "See all results" footer; no-results state.
 *
 * The dropdown MUST NOT inherit the input's width — the ported CSS handles it
 * (v18.css:3): width:max(100%,440px); max-width:min(92vw,560px); right:0.
 * Visibility is the `open` class, so the box is ALWAYS rendered — same
 * contract as the mega-menu panel, and conditionally rendering it would leave
 * it display:none while mounted.
 *
 * The prototype ran `eval(el.dataset.act)` on click (:6345). Here every result
 * carries a plain href and the client navigates — no eval, no code strings in
 * the DOM.
 */

const RECENT_KEY = 'maktech_recent_searches';

/** recentSearches()/pushRecent() (:6288) — max 5, case-insensitive dedupe. */
function readRecent(): string[] {
  try {
    const v = localStorage.getItem(RECENT_KEY);
    return v ? (JSON.parse(v) as string[]) : [];
  } catch {
    return [];
  }
}
function pushRecent(q: string): void {
  const t = q.trim();
  if (!t) return;
  try {
    const r = [t, ...readRecent().filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, 5);
    localStorage.setItem(RECENT_KEY, JSON.stringify(r));
  } catch {
    /* private mode — a search box is not worth crashing over */
  }
}

/**
 * sgHL() (:6281) — highlight the matched run.
 *
 * The prototype escapes BEFORE searching, then builds an HTML string. Here
 * React escapes on render, so the text is SPLIT into plain strings and a
 * <mark>. Same result, and there is no HTML string to get wrong: a user typing
 * `<img onerror=...>` is text at every step, never markup.
 */
function Highlight({ text, q }: { text: string; q: string }) {
  const needle = q.trim();
  if (!needle) return <>{text}</>;
  const i = text.toLowerCase().indexOf(needle.toLowerCase());
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <mark className="rounded-[3px] bg-[rgba(244,80,42,.18)] px-px font-extrabold text-[var(--cobalt-dark)]">{text.slice(i, i + needle.length)}</mark>
      {text.slice(i + needle.length)}
    </>
  );
}

export function SearchBox() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(-1);
  const [recent, setRecent] = useState<string[]>([]);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce: the prototype filtered in-memory so it could run per keystroke.
  // This hits the network, so a 150ms pause turns "javascript" from 10
  // requests into 1 without any perceptible lag.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), 150);
    return () => clearTimeout(t);
  }, [value]);

  const { data } = useSearch(debounced, open);

  // closeSuggest (:6350) — click-away.
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, []);

  const runSearch = useCallback(
    (q: string) => {
      const t = q.trim();
      // runSearch (:6354) opens with `if(inp)inp.value=q` — the term STAYS in
      // the box after searching. It matters for the two callers that don't
      // already have it there: a trending chip and a recent-search row both put
      // their own text into the input.
      setValue(t);
      pushRecent(t);
      setRecent(readRecent());
      setOpen(false);
      setIndex(-1);
      navigate(t ? `/courses?search=${encodeURIComponent(t)}` : '/courses');
    },
    [navigate],
  );

  const goTo = useCallback(
    (href: string) => {
      pushRecent(value);
      setRecent(readRecent());
      setOpen(false);
      setIndex(-1);
      navigate(href);
    },
    [navigate, value],
  );

  const results = data?.results ?? [];
  const trending = data?.trending ?? [];
  const groups = [...new Set(results.map((r) => r.group))];
  const hasQuery = debounced.trim().length > 0;

  /**
   * The flat keyboard list. It must match the RENDER order exactly, or ↓ moves
   * the highlight to a different row than the one Enter activates.
   */
  const flat: { kind: 'recent' | 'result' | 'all'; label: string; href?: string }[] = hasQuery
    ? results.length
      ? [
          ...groups.flatMap((g) =>
            results.filter((r) => r.group === g).map((r) => ({ kind: 'result' as const, label: r.label, href: r.href })),
          ),
          { kind: 'all' as const, label: debounced },
        ]
      : []
    : recent.map((r) => ({ kind: 'recent' as const, label: r }));

  function activate(i: number) {
    const item = flat[i];
    if (!item) return;
    if (item.kind === 'result' && item.href) goTo(item.href);
    else runSearch(item.label);
  }

  // suggestKey (:6367)
  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setIndex((i) => (flat.length ? (i + 1 + flat.length) % flat.length : -1));
      return;
    }
    /**
     * PROTOTYPE BUG, PORTED AS-IS — reported, not silently fixed.
     *
     * moveSuggest (:6363) is `sgIndex=(sgIndex+d+n)%n` and sgIndex starts at
     * -1, so ArrowUp on a fresh dropdown computes (-1-1+n)%n = n-2 and lands on
     * the SECOND-to-last row, not the last. (ArrowDown is fine: (-1+1+n)%n = 0.)
     * Spec 7.3 only says "Keyboard: ↑/↓/Enter/Escape" — it does not specify wrap
     * semantics, so there is no spec/prototype conflict for the spec to win, and
     * the brief is explicit that the prototype defines behaviour. A one-line
     * `i <= -1 ? flat.length - 1 : ...` fixes it whenever you want it fixed.
     */
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndex((i) => (flat.length ? (i - 1 + flat.length) % flat.length : -1));
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      setIndex(-1);
      inputRef.current?.blur();
      return;
    }
    if (e.key === 'Enter') {
      if (index >= 0 && flat[index]) {
        e.preventDefault();
        activate(index);
        return;
      }
      runSearch(value);
    }
  }

  let cursor = -1;

  return (
    <div className="relative w-[420px] min-w-[190px] flex-[0_1_420px] max-[980px]:hidden min-[1131px]:max-[1520px]:w-auto min-[1131px]:max-[1520px]:flex-1" ref={wrapRef}>
      <svg className="absolute top-1/2 left-[13px] size-4 -translate-y-1/2 stroke-[var(--ink-soft)]" viewBox="0 0 24 24" fill="none" strokeWidth="2">
        <circle cx="11" cy="11" r="7" />
        <path d="M21 21l-4.3-4.3" />
      </svg>
      <input
        id="navSearch"
        data-testid="nav-search"
        type="text"
        autoComplete="off"
        placeholder={t('nav.search_placeholder')}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setIndex(-1);
          setOpen(true);
        }}
        onFocus={() => {
          setRecent(readRecent());
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        role="combobox"
        aria-expanded={open}
        aria-controls="sgBox"
        aria-autocomplete="list"
        className="w-full rounded-full border-[1.5px] border-[var(--line)] bg-[var(--paper)] py-[13px] pr-4 pl-11 text-[15px] outline-none focus:border-[var(--cobalt)] focus:bg-white"
      />

      {/* ALWAYS rendered; `open` drives visibility (v18.css:7). */}
      <div className={`absolute top-[calc(100%+10px)] right-0 z-[90] max-h-[min(70vh,520px)] w-[max(100%,440px)] max-w-[min(92vw,560px)] overflow-y-auto rounded-2xl border border-[var(--line)] bg-[var(--card)] p-2 text-left shadow-[var(--shadow-lg)] ${open ? 'block [animation:navIn_.16s_ease]' : 'hidden'}`} id="sgBox" data-testid="sg-box" role="listbox">
        {!hasQuery ? (
          <>
            {recent.length ? (
              <div className="px-1 py-1.5">
                <span className="flex items-center justify-between px-2 pt-1 pb-[7px] font-['Space_Grotesk',sans-serif] text-[10.5px] font-bold tracking-[.12em] text-[var(--cobalt)] uppercase">
                  {t('nav.search.recent')}
                  <button
                    className="border-0 bg-transparent text-[10.5px] font-bold tracking-[.06em] text-[var(--ink-soft)] normal-case hover:text-[var(--cobalt)]"
                    data-testid="sg-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      localStorage.removeItem(RECENT_KEY);
                      setRecent([]);
                    }}
                  >
                    {t('nav.search.clear')}
                  </button>
                </span>
                {recent.map((r) => {
                  cursor++;
                  const mine = cursor;
                  return (
                    <button
                      className={`flex w-full cursor-pointer items-center gap-[11px] rounded-[10px] border-0 px-2 py-[9px] text-left transition duration-[120ms] ${index === mine ? 'bg-[var(--paper)]' : 'bg-transparent hover:bg-[var(--paper)]'}`}
                      key={r}
                      data-testid={`sg-recent-${r}`}
                      onClick={() => runSearch(r)}
                    >
                      <span className="grid size-8 flex-none place-items-center rounded-[9px] bg-[var(--paper)] text-[var(--ink-soft)]">
                        <HiOutlineClock className="size-4" aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1">
                        <b className="block overflow-hidden text-[13.5px] font-semibold text-ellipsis whitespace-nowrap text-[var(--ink)]">{r}</b>
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : null}
            <div className="border-t border-[var(--line-2)] px-1 py-1.5">
              <span className="flex items-center justify-between px-2 pt-1 pb-[7px] font-['Space_Grotesk',sans-serif] text-[10.5px] font-bold tracking-[.12em] text-[var(--cobalt)] uppercase">{t('nav.search.trending')}</span>
              <div className="flex flex-wrap gap-1.5 px-1 pb-1">
                {(trending.length ? trending : ['React', 'Python', 'IELTS', 'Excel', 'Free courses', 'UI/UX']).map((t) => (
                  <button className="rounded-full border border-[var(--line)] bg-[var(--paper)] px-3 py-[5px] text-xs font-semibold text-[var(--ink-soft)] transition duration-[120ms] hover:border-[var(--cobalt)] hover:bg-[var(--card)] hover:text-[var(--cobalt)]" key={t} data-testid={`sg-trend-${t}`} onClick={() => runSearch(t)}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : results.length === 0 ? (
          <div className="px-4 py-[26px] text-center" data-testid="sg-empty">
            <HiOutlineSearch className="mx-auto mb-2 size-8 text-[var(--ink-soft)] opacity-50" aria-hidden />
            <b className="mb-1 block text-sm">{t('nav.search.no_matches', { query: debounced })}</b>
            <p className="mb-3 text-[12.5px] text-[var(--ink-soft)]">{t('nav.search.no_matches_hint')}</p>
            <button
              className="inline-flex items-center justify-center gap-2 rounded-full border-[1.5px] border-[var(--line)] bg-[var(--card)] px-4 py-[9px] text-[13.5px] font-semibold text-[var(--ink)] hover:border-[var(--cobalt)] hover:text-[var(--cobalt)]"
              onClick={() => {
                setOpen(false);
                navigate('/courses');
              }}
            >
              {t('nav.search.browse_all')}
            </button>
          </div>
        ) : (
          <>
            {groups.map((g) => (
              <div className="px-1 py-1.5 [&+&]:mt-1 [&+&]:border-t [&+&]:border-[var(--line-2)]" key={g}>
                <span className="flex items-center justify-between px-2 pt-1 pb-[7px] font-['Space_Grotesk',sans-serif] text-[10.5px] font-bold tracking-[.12em] text-[var(--cobalt)] uppercase">{g}</span>
                {results
                  .filter((r) => r.group === g)
                  .map((r) => {
                    cursor++;
                    const mine = cursor;
                    return (
                      <button
                        className={`group flex w-full cursor-pointer items-center gap-[11px] rounded-[10px] border-0 px-2 py-[9px] text-left transition duration-[120ms] ${index === mine ? 'bg-[var(--paper)]' : 'bg-transparent hover:bg-[var(--paper)]'}`}
                        key={`${r.group}-${r.href}-${r.label}`}
                        data-testid={`sg-item-${r.label}`}
                        role="option"
                        aria-selected={index === mine}
                        onClick={() => goTo(r.href)}
                      >
                        <span className="grid size-8 flex-none place-items-center rounded-[9px] bg-[var(--paper)] text-[var(--cobalt)] group-hover:bg-[var(--card)]">
                          <SearchResultIcon group={r.group} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <b className="block overflow-hidden text-[13.5px] font-semibold text-ellipsis whitespace-nowrap text-[var(--ink)]">
                            <Highlight text={r.label} q={debounced} />
                          </b>
                          <span className="block overflow-hidden text-[11.5px] text-ellipsis whitespace-nowrap text-[var(--ink-soft)]">{r.sub}</span>
                        </span>
                        <span className="flex-none text-[11px] text-[var(--ink-soft)] opacity-0 group-hover:opacity-100">↵</span>
                      </button>
                    );
                  })}
              </div>
            ))}
            {(() => {
              cursor++;
              const mine = cursor;
              return (
                <button
                  className={`mt-1 w-full rounded-[10px] border-0 p-[11px] text-[12.5px] font-semibold transition duration-[120ms] ${index === mine ? 'bg-[var(--ink)] text-white' : 'bg-[var(--paper)] text-[var(--ink-soft)] hover:bg-[var(--ink)] hover:text-white'}`}
                  data-testid="sg-all"
                  onClick={() => runSearch(debounced)}
                >
                  {t('nav.search.see_all', { query: debounced })} →
                </button>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
}
